import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAdminResetPasswordMutation } from '../../redux/services';
import { useIsMobile } from '../../hooks/use-is-mobile';

export const ResetPasswordDialog = ({ open, user, onClose }) => {
  const [tempPassword, setTempPassword] = useState(null);
  const isMobile = useIsMobile();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [adminResetPassword, { isLoading }] = useAdminResetPasswordMutation();

  const handleConfirm = async () => {
    setError(null);
    try {
      const result = await adminResetPassword(user._id).unwrap();
      setTempPassword(result.tempPassword);
    } catch (err) {
      // El servidor responde 404 —no 403— sobre una cuenta administrativa cuando falta
      // `users.reset-admin-password`: no confirma qué cuentas lo son. La acción ya no
      // debería estar visible en ese caso, así que este mensaje es la red de seguridad.
      setError(
        err?.status === 404
          ? 'No se pudo resetear. Si es una cuenta administrativa hace falta el permiso «Resetear la contraseña de un administrador».'
          : 'No se pudo resetear la contraseña. Intenta de nuevo.',
      );
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setTempPassword(null);
    setCopied(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth fullScreen={isMobile}>
      {!tempPassword ? (
        <>
          <DialogTitle>Resetear contraseña</DialogTitle>
          <DialogContent>
            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
            <Typography>
              ¿Resetear la contraseña de <strong>{user?.username}</strong>? Se generará una contraseña temporal que deberás compartir con el usuario.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isLoading}>Cancelar</Button>
            <Button onClick={handleConfirm} variant='contained' disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} /> : 'Confirmar'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Contraseña temporal generada</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Comparte esta contraseña con <strong>{user?.username}</strong>. El usuario deberá cambiarla al iniciar sesión.
            </Typography>
            <TextField
              value={tempPassword}
              fullWidth
              inputProps={{ readOnly: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton onClick={handleCopy} edge='end'>
                      <ContentCopyIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={copied ? 'Copiado al portapapeles' : ' '}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant='contained'>Cerrar</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
